<script setup lang="ts">
interface FaqItem {
  question: string;
  answer: FaqAnswerBlock[];
}

interface FaqListItem {
  title?: string;
  text: string;
}

type FaqAnswerBlock =
  | { type: "paragraph"; text: string }
  | {
      type: "ordered-list" | "unordered-list";
      items: FaqListItem[];
    }
  | { type: "contact"; text: string; email: string };

const isVisible = ref(false);
const route = useRoute();

const isQuestionaryLayout = computed(() => route.meta.layout === "questionary");

const faqItems: FaqItem[] = [
  {
    question: "¿En qué consiste el programa?",
    answer: [
      {
        type: "paragraph",
        text: "El programa tiene como objetivo fortalecer las oportunidades de inserción laboral de las participantes mediante el cierre de brechas en habilidades relevantes para el mercado laboral actual en Chile. Para ello, contempla tres etapas: la evaluación de habilidades actuales y la identificación de brechas respecto de perfiles ocupacionales de demanda activa, la implementación de un piloto de capacitación gratuita orientado a cerrar dichas brechas, y un proceso de intermediación laboral para apoyar la inserción de las egresadas en ocupaciones acordes a las habilidades desarrolladas."
      }
    ]
  },
  {
    question: "¿Qué puedo obtener a través del programa?",
    answer: [
      {
        type: "paragraph",
        text: "El programa incluye tres componentes:"
      },
      {
        type: "ordered-list",
        items: [
          {
            title: "Diagnóstico de habilidades",
            text: "Un análisis de sus habilidades actuales y una comparación con los requerimientos de ocupaciones de interés para el mercado laboral en Chile, permitiendo identificar áreas de desarrollo prioritarias."
          },
          {
            title: "Capacitación",
            text: "Un subconjunto de las personas que completen el diagnóstico será seleccionado para participar en un piloto formativo gratuito, estructurado para cerrar las brechas de habilidades identificadas."
          },
          {
            title: "Intermediación laboral",
            text: "Las personas que completen el proceso formativo contarán con apoyo activo para vincularse con empleadores y acceder a oportunidades laborales acordes a las competencias desarrolladas."
          }
        ]
      }
    ]
  },
  {
    question: "¿Quiénes pueden participar?",
    answer: [
      {
        type: "paragraph",
        text: "Para participar en el programa se deben cumplir los siguientes requisitos:"
      },
      {
        type: "unordered-list",
        items: [
          {
            text: "Ser mujer, migrante, y contar con permiso de residencia definitiva ya otorgado."
          },
          {
            text: "Residir en la Región Metropolitana, preferentemente en alguna de las 11 comunas que forman parte del proyecto."
          }
        ]
      },
      {
        type: "paragraph",
        text: "Estos requisitos serán verificados antes del inicio del piloto formativo para las personas seleccionadas."
      }
    ]
  },
  {
    question: "¿Cuáles son los pasos del proceso?",
    answer: [
      {
        type: "paragraph",
        text: "El proceso de postulación y evaluación contempla los siguientes pasos:"
      },
      {
        type: "ordered-list",
        items: [
          {
            text: "Registrarse en la plataforma Recarga tu Futuro y verificar el correo electrónico."
          },
          {
            text: "Completar el formulario inicial para compartir antecedentes sobre su trayectoria formativa y laboral."
          },
          {
            text: "Completar el cuestionario de habilidades para identificar sus conocimientos actuales."
          },
          {
            text: "Esperar la comunicación del programa con el resultado de la selección y los pasos siguientes."
          }
        ]
      },
      {
        type: "paragraph",
        text: "Completar ambos formularios sirve como postulación al programa y permite construir un inventario de habilidades para orientar las siguientes etapas."
      }
    ]
  },
  {
    question: "¿Por qué se solicita completar cuestionarios?",
    answer: [
      {
        type: "paragraph",
        text: "Los cuestionarios permiten recopilar información sobre sus habilidades, experiencia e intereses laborales. Con base en esa información, el programa identifica las áreas de desarrollo más relevantes para cada persona y orienta su asignación al itinerario formativo más adecuado. Los resultados también son el criterio principal para la selección de participantes del piloto formativo."
      }
    ]
  },
  {
    question: "¿Tiene algún costo participar en el programa?",
    answer: [
      {
        type: "paragraph",
        text: "No. Tanto el diagnóstico de habilidades como la capacitación son completamente gratuitos para las participantes seleccionadas."
      }
    ]
  },
  {
    question:
      "¿Qué ocurre si completo el diagnóstico, pero no soy seleccionada para la capacitación?",
    answer: [
      {
        type: "paragraph",
        text: "El diagnóstico tiene valor independiente del proceso de selección: entrega información sobre sus habilidades actuales y sobre áreas en las que podría continuar desarrollándose. Ese resultado es de su propiedad y puede ser utilizado para orientar su búsqueda de empleo o sus decisiones de formación futura."
      }
    ]
  },
  {
    question: "¿Se entrega alguna certificación al completar la capacitación?",
    answer: [
      {
        type: "paragraph",
        text: "Sí. Las participantes que cumplan con los requisitos de asistencia y aprobación de cada módulo recibirán una certificación de las habilidades adquiridas. Esta certificación está orientada a facilitar la búsqueda de empleo y el proceso de intermediación laboral que sigue al piloto formativo."
      }
    ]
  },
  {
    question: "¿En qué consiste la intermediación laboral?",
    answer: [
      {
        type: "paragraph",
        text: "La intermediación laboral es el proceso mediante el cual el programa apoya activamente la vinculación de las egresadas con empleadores que requieren los perfiles desarrollados durante la capacitación. Este proceso se realiza en coordinación con las Oficinas Municipales de Intermediación Laboral (OMIL) de las comunas participantes, que cuentan con vínculos directos con empresas locales. El objetivo es facilitar el acceso a empleos formales acordes a las competencias adquiridas."
      }
    ]
  },
  {
    question: "¿Con quién puedo comunicarme si tengo consultas adicionales?",
    answer: [
      {
        type: "contact",
        text: "Para consultas adicionales, escribir directamente a la casilla de correo del programa:",
        email: "contacto@recargatufuturo.cl"
      }
    ]
  }
];

const openFaq = () => {
  isVisible.value = true;
};

const closeFaq = () => {
  isVisible.value = false;
};
</script>

<template>
  <div class="faq-access">
    <UiButton
      type="is-primary"
      icon-left="circle-question"
      :class="[
        'faq-trigger',
        { 'faq-trigger--questionary': isQuestionaryLayout }
      ]"
      title="Preguntas frecuentes"
      aria-label="Abrir preguntas frecuentes"
      aria-haspopup="dialog"
      :aria-expanded="isVisible"
      aria-controls="frequent-questions"
      @click="openFaq"
    >
      <span>Preguntas frecuentes</span>
    </UiButton>

    <UiModalCard
      v-model="isVisible"
      class="faq-modal"
      title="Preguntas frecuentes"
      :scroll="true"
      @close="closeFaq"
    >
      <template #body>
        <div id="frequent-questions" class="faq-content">
          <p class="faq-introduction">
            Encuentra respuestas rápidas sin perder el avance de tu proceso.
          </p>

          <div class="faq-list">
            <details
              v-for="item in faqItems"
              :key="item.question"
              class="faq-item"
            >
              <summary>{{ item.question }}</summary>
              <div class="faq-answer">
                <template
                  v-for="(block, blockIndex) in item.answer"
                  :key="blockIndex"
                >
                  <p v-if="block.type === 'paragraph'">{{ block.text }}</p>
                  <ol v-else-if="block.type === 'ordered-list'">
                    <li v-for="listItem in block.items" :key="listItem.text">
                      <strong v-if="listItem.title">
                        {{ listItem.title }}:
                      </strong>
                      {{ listItem.text }}
                    </li>
                  </ol>
                  <ul v-else-if="block.type === 'unordered-list'">
                    <li v-for="listItem in block.items" :key="listItem.text">
                      {{ listItem.text }}
                    </li>
                  </ul>
                  <p v-else>
                    {{ block.text }}
                    <a class="faq-contact-link" :href="`mailto:${block.email}`">
                      {{ block.email }}
                    </a>
                  </p>
                </template>
              </div>
            </details>
          </div>
        </div>
      </template>

      <template #footer>
        <footer class="faq-footer">
          <UiButton type="is-primary" @click="closeFaq">Cerrar</UiButton>
        </footer>
      </template>
    </UiModalCard>
  </div>
</template>

<style scoped lang="scss">
.faq-access {
  position: relative;
  z-index: 35;
}

.faq-trigger {
  position: fixed;
  right: max($spacing_4, env(safe-area-inset-right));
  bottom: max($spacing_4, env(safe-area-inset-bottom));
  box-shadow: 0 8px 24px rgba($primary-dark, 0.22);
}

.faq-trigger--questionary {
  top: max($spacing_4, env(safe-area-inset-top));
  bottom: auto;
}

.faq-content {
  display: flex;
  flex-direction: column;
  gap: $spacing_5;
}

.faq-introduction {
  color: $text;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: $spacing_3;
}

.faq-item {
  overflow: hidden;
  border: 1px solid rgba($primary-dark, 0.14);
  border-radius: $spacing_3;
  background: $white;

  summary {
    position: relative;
    padding: $spacing_4 $spacing_7 $spacing_4 $spacing_4;
    color: $primary-dark;
    font-family: $family-heading;
    font-weight: 600;
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    &::after {
      content: "+";
      position: absolute;
      top: 50%;
      right: $spacing_4;
      color: $secondary;
      font-size: 1.5rem;
      font-weight: 400;
      transform: translateY(-50%);
    }
  }

  &[open] summary {
    border-bottom: 1px solid rgba($primary-dark, 0.1);

    &::after {
      content: "−";
    }
  }
}

.faq-answer {
  display: flex;
  padding: $spacing_4;
  flex-direction: column;
  gap: $spacing_3;
  color: $text;

  ol,
  ul {
    padding-left: $spacing_5;
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }

  li + li {
    margin-top: $spacing_2;
  }
}

.faq-contact-link {
  color: $secondary;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.faq-footer {
  display: flex;
  justify-content: flex-end;
  padding: $spacing_4 $spacing_5;
  border-top: 1px solid rgba($primary-dark, 0.12);
  background: $white-bis;
}

:deep(.modal-card-body) {
  overflow-y: auto;
}

@media print {
  .faq-trigger {
    display: none;
  }
}
</style>
